import { memo, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@shared/components/ui/input';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { TooltipContent, TooltipProvider, TooltipSH, TooltipTrigger } from '@shared/components/ui/shadcn/tooltip';
import type { ReadyToPrepareByHourRow } from '../../models';
import { aggregateByClientHourRanges, type ClientHourRangeRow } from '../../utils/aggregateByClientHourRanges';

interface ClientHourRangesChartProps {
  rows: ReadyToPrepareByHourRow[];
}

const PCT_LABEL_MIN = 4;

function formatPctShort(pct: number): string {
  if (pct == null || Number.isNaN(pct)) return '';
  return `${pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%`;
}

/** Etiqueta en el tramo: cantidad + % (el % solo si hay espacio visual). */
function SegmentInlineLabel({ count, pct }: { count: number; pct: number }) {
  if (count === 0) return null;
  const showPct = pct >= PCT_LABEL_MIN;
  return (
    <span className="flex flex-col items-center justify-center gap-0 px-0.5 text-center leading-none">
      <span className="tabular-nums text-[11px] font-bold tracking-tight">{count}</span>
      {showPct && <span className="mt-0.5 text-[9px] font-semibold opacity-95">{formatPctShort(pct)}</span>}
    </span>
  );
}

function RowTooltipBody({ row }: { row: ClientHourRangeRow }) {
  const { t } = useTranslation('readyToPrepareByHourModule');
  const label12_14 = t('chartByClient.range_12_14', '12–14 h');
  const label14_16 = t('chartByClient.range_14_16', '14–16 h');
  const label16_12 = t('chartByClient.range_16_12', '16–12 h');

  const items = [
    { name: label12_14, count: row.range_12_14, pct: row.pct_12_14, color: 'hsl(var(--chart-1))' },
    { name: label14_16, count: row.range_14_16, pct: row.pct_14_16, color: 'hsl(var(--chart-2))' },
    { name: label16_12, count: row.range_16_12, pct: row.pct_16_12, color: 'hsl(var(--chart-3))' },
  ];

  return (
    <div className="text-left text-xs">
      <p className="font-medium text-foreground mb-1">{row.client_name}</p>
      <p className="text-muted-foreground mb-1.5">
        {t('chartByClient.tooltip_share_intro', 'Distribución del 100 % (pedidos por franja)')}
      </p>
      <ul className="space-y-0.5 text-muted-foreground">
        {items.map((item) => (
          <li key={item.name} className="flex items-center gap-2">
            <span
              className="inline-block size-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: item.color }}
              aria-hidden
            />
            <span>
              {item.name}:{' '}
              <span className="tabular-nums text-foreground">
                {item.count} ({item.pct.toFixed(1)}%)
              </span>
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-1.5 pt-1.5 border-t border-border text-muted-foreground tabular-nums">
        {t('chartByClient.tooltip_total', 'Total')}: {row.total}
      </p>
    </div>
  );
}

const ClientStackRow = memo(function ClientStackRow({ row }: { row: ClientHourRangeRow }) {
  const p1 = row.pct_12_14;
  const p2 = row.pct_14_16;
  const p3 = row.pct_16_12;
  const c1 = row.range_12_14;
  const c2 = row.range_14_16;
  const c3 = row.range_16_12;

  return (
    <TooltipSH>
      <TooltipTrigger asChild>
        <div
          className="grid grid-cols-1 gap-x-3 gap-y-2 py-2 min-[520px]:grid-cols-[minmax(12rem,22rem)_minmax(0,1fr)] min-[520px]:items-center min-[520px]:gap-y-0"
          data-testid="ready-to-prepare-client-row"
        >
          <div
            className="break-words text-left text-xs leading-snug text-muted-foreground min-[520px]:pr-1"
            title={row.client_name}
          >
            {row.client_name}
          </div>
          <div
            className="flex min-h-[2.5rem] w-full min-w-0 overflow-hidden rounded-md ring-1 ring-border/70 transition-colors"
            role="img"
            aria-label={`${row.client_name}: ${c1} pedidos (${p1.toFixed(0)}%), ${c2} (${p2.toFixed(0)}%), ${c3} (${p3.toFixed(0)}%)`}
          >
            <div
              className="flex h-full min-h-[2.5rem] shrink-0 items-center justify-center overflow-hidden text-white"
              style={{
                flex: `0 0 ${p1}%`,
                minWidth: p1 > 0 ? 2 : 0,
                backgroundColor: 'hsl(var(--chart-1))',
                textShadow: '0 1px 2px rgb(0 0 0 / 45%)',
              }}
            >
              <SegmentInlineLabel count={c1} pct={p1} />
            </div>
            <div
              className="flex h-full min-h-[2.5rem] shrink-0 items-center justify-center overflow-hidden text-white"
              style={{
                flex: `0 0 ${p2}%`,
                minWidth: p2 > 0 ? 2 : 0,
                backgroundColor: 'hsl(var(--chart-2))',
                textShadow: '0 1px 2px rgb(0 0 0 / 45%)',
              }}
            >
              <SegmentInlineLabel count={c2} pct={p2} />
            </div>
            <div
              className="flex h-full min-h-[2.5rem] shrink-0 items-center justify-center overflow-hidden text-white"
              style={{
                flex: `0 0 ${p3}%`,
                minWidth: p3 > 0 ? 2 : 0,
                backgroundColor: 'hsl(var(--chart-3))',
                textShadow: '0 1px 2px rgb(0 0 0 / 45%)',
              }}
            >
              <SegmentInlineLabel count={c3} pct={p3} />
            </div>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className="max-w-xs border border-border bg-card px-3 py-2 text-foreground shadow-md"
      >
        <RowTooltipBody row={row} />
      </TooltipContent>
    </TooltipSH>
  );
});

export const ClientHourRangesChart = ({ rows }: ClientHourRangesChartProps) => {
  const { t } = useTranslation('readyToPrepareByHourModule');
  const [clientSearch, setClientSearch] = useState('');

  const chartData = useMemo(() => aggregateByClientHourRanges(rows).filter((r) => r.total > 0), [rows]);

  const filteredData = useMemo(() => {
    const raw = clientSearch.trim();
    if (!raw) return chartData;
    const q = raw.toLowerCase();
    const idMatch = /^\d+$/.test(raw) ? Number(raw) : null;
    return chartData.filter((r) => {
      if (idMatch !== null && r.client_team_id === idMatch) return true;
      return r.client_name.toLowerCase().includes(q);
    });
  }, [chartData, clientSearch]);

  const label12_14 = t('chartByClient.range_12_14', '12–14 h');
  const label14_16 = t('chartByClient.range_14_16', '14–16 h');
  const label16_12 = t('chartByClient.range_16_12', '16–12 h');

  if (chartData.length === 0) {
    return null;
  }

  return (
    <SectionBox
      title={t('chartByClient.title', 'Pedidos por cliente y franja horaria')}
      data-testid="ready-to-prepare-client-ranges"
    >
      <p className="text-xs text-muted-foreground -mt-2 mb-3 max-w-3xl">
        {t(
          'chartByClient.hint',
          'Cada barra es el 100 % de los pedidos del cliente. En cada color: número de pedidos arriba y porcentaje debajo (si cabe).',
        )}
      </p>

      <div className="relative mb-3 max-w-md">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          value={clientSearch}
          onChange={(e) => setClientSearch(e.target.value)}
          placeholder={t('chartByClient.search_placeholder', 'Buscar cliente…')}
          className="bg-card-bg border-border pl-9 transition-colors"
          aria-label={t('chartByClient.search_label', 'Buscar por nombre o ID de cliente')}
          data-testid="ready-to-prepare-client-search"
        />
      </div>

      <TooltipProvider delayDuration={280} skipDelayDuration={400}>
        <div className="space-y-1 w-full min-w-0">
          {/* Escala 0–100 % (solo referencia visual, sin SVG) */}
          <div className="grid grid-cols-1 gap-x-3 min-[520px]:grid-cols-[minmax(12rem,22rem)_minmax(0,1fr)] items-end">
            <div className="hidden min-[520px]:block" aria-hidden />
            <div className="flex justify-between px-0.5 text-[10px] tabular-nums text-muted-foreground select-none">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Leyenda */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pb-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2.5 rounded-sm" style={{ backgroundColor: 'hsl(var(--chart-1))' }} />
              {label12_14}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2.5 rounded-sm" style={{ backgroundColor: 'hsl(var(--chart-2))' }} />
              {label14_16}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2.5 rounded-sm" style={{ backgroundColor: 'hsl(var(--chart-3))' }} />
              {label16_12}
            </span>
          </div>

          <div className="max-h-[min(520px,65vh)] overflow-y-auto overscroll-contain pr-1 [contain:content]">
            {filteredData.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('chartByClient.search_empty', 'Ningún cliente coincide con la búsqueda.')}
              </p>
            ) : (
              filteredData.map((row) => <ClientStackRow key={row.client_team_id} row={row} />)
            )}
          </div>
        </div>
      </TooltipProvider>
    </SectionBox>
  );
};
